import { useCallback, useMemo } from 'react';
import type {
  CustomChartDefinition,
  CustomChartTableSettings,
  CustomChartTableView,
  DashboardPreset,
  DashboardPresetContent,
  DashboardPresetFilters,
} from '@/entities/dashboards/model/api/types';
import { useHasPermission } from '@/entities/profile';
import {
  toSharedPresetListFields,
  useSharedPresets,
  useSharePreset,
  type SharedPreset,
  type SharedPresetTransfer,
  type SharedPresetUser,
} from '@/entities/shared-presets';
import { useCreateSystemPreset, useSystemPresets } from '@/entities/system-presets';
import { isQueryDataReceived } from '@/shared/api';
import { readLegacyChartTableView, readOwnChartTableView, withChartTableView } from './dashboardChartTableView';
import type { DashboardChartTablesColumnsPanel } from './types';
import {
  buildSharedDashboardPresetId,
  buildSystemDashboardPresetId,
  DASHBOARD_PRESET_KEY_PARSERS,
  DASHBOARD_PRESETS_TABLE_NAME,
  parseSharedDashboardPresetId,
} from './dashboardPresetKeys';
import {
  type DashboardPresetChanges,
  readServerFilters,
  useServerDashboardPresetWrite,
} from './useServerDashboardPresetWrite';
import { useServerPresetMutations } from './useServerPresetMutations';
import { useSettings } from './useSettings';

/** Дашборд в списке страницы дашбордов: личный, совместный либо системный, с правами на него. */
export type DashboardPresetItem = DashboardPreset & {
  /** Дашборд системный — общий для всех пользователей */
  isSystem: boolean;
  /** Дашборд совместный — им владелец поделился с выбранными пользователями */
  isShared: boolean;
  /** Владелец совместного дашборда */
  owner?: SharedPresetUser;
  /** Получатели совместного дашборда — известны только его владельцу */
  recipients?: SharedPresetUser[];
  /**
   * Текущий пользователь вправе править дашборд: свой личный, совместный, которым владеет, либо
   * системный, который он сам сделал системным
   */
  isEditable: boolean;
  /**
   * У автора системного дашборда есть изменения фильтров или вида колонок, ещё не записанные в
   * дашборд: пока он их не сохранит, не отменит или не унесёт в копию, они действуют только у него
   */
  hasUnsavedChanges: boolean;
};

const byName = (a: DashboardPresetItem, b: DashboardPresetItem): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

/**
 * Фильтры панели и вид колонок таблиц дашборда пользователь меняет у себя, а не в самом дашборде:
 * серверный дашборд, который он не правит, и системный — даже у автора: смена автора действует
 * только у него, пока он её не сохранит.
 */
const isOwnFiltersHolder = (preset: Pick<DashboardPresetItem, 'isSystem' | 'isEditable'>): boolean =>
  preset.isSystem || !preset.isEditable;

/**
 * Хук работы с пользовательскими дашбордами-пресетами страницы дашбордов.
 *
 * Пресет здесь — самостоятельный состав диаграмм, а не снимок текущего вида экрана: создание
 * заводит новый дашборд, а правка меняет его состав. Поэтому «сохранить текущее состояние в
 * пресет» — операции, обратной применению, — у дашбордов нет.
 *
 * Личные дашборды лежат в настройках пользователя, системные и совместные — на сервере и попадают в
 * тот же список: системные первыми, за ними совместные, затем личные. У совместного личные применение
 * и закрепление, а название, состав и фильтры панели общие для владельца и получателей. У системного
 * личное только применение: название, состав, фильтры и закрепление общие для всех, а ведёт их
 * автор — тот, кто сделал дашборд системным; разрешение на системные пресеты без авторства их не
 * открывает. Пока системные и совместные не получены, отсутствие дашборда в списке ещё ничего не
 * означает (`isPresetListKnown`).
 *
 * Фильтры панели тот, кто серверный дашборд не правит (получатель совместного, не автор системного),
 * вправе сменить у себя: смена запоминается за ним и действует, пока он её не сбросит, — правка фильтров
 * самого дашборда до него тем временем не доходит.
 */
export const useDashboardPresets = () => {
  const {
    userId,
    dashboardPresets,
    createDashboardPreset,
    updateDashboardPreset,
    applyDashboardPreset,
    clearActiveDashboardPreset,
    deleteDashboardPreset,
    setDashboardPresetPinned,
    setDashboardChartTableSettings,
    clearDashboardChartTableViews,
    setDashboardChartTablesColumnsPanel,
    setDashboardSharedFilters,
    pruneDashboardSharedFilters,
  } = useSettings();

  const canManageSystemPresets = useHasPermission('ADMIN_SYSTEM_PRESET');
  const systemPresetsQuery = useSystemPresets<DashboardPresetContent>(DASHBOARD_PRESETS_TABLE_NAME);
  const systemPresetsData = systemPresetsQuery.data;
  const { mutateAsync: createSystemPreset, isPending: isMakingSystem } =
    useCreateSystemPreset<DashboardPresetContent>();
  const sharedPresetsQuery = useSharedPresets<DashboardPresetContent>(DASHBOARD_PRESETS_TABLE_NAME);
  const sharedPresetsData = sharedPresetsQuery.data;
  const { writeServerPreset, isUpdating } = useServerDashboardPresetWrite();
  const { pinServerPreset, deleteServerPreset } = useServerPresetMutations(DASHBOARD_PRESET_KEY_PARSERS);

  const sharedFilters = dashboardPresets.sharedFilters;
  const chartTables = dashboardPresets.chartTables;

  const systemPresets = useMemo<DashboardPresetItem[]>(() => {
    const list = (systemPresetsData?.items ?? []).map<DashboardPresetItem>((preset) => {
      const id = buildSystemDashboardPresetId(preset.id);
      const { charts } = preset.settings;
      return {
        id,
        name: preset.name,
        pinned: preset.isPinned,
        charts,
        filters: readServerFilters(preset.settings),
        isSystem: true,
        isShared: false,
        isEditable: preset.isOwner,
        // У прочих пользователей своя смена — их собственная, сохранять её в дашборд им нечем
        hasUnsavedChanges:
          preset.isOwner &&
          (sharedFilters?.[id] !== undefined ||
            charts.some((chart) => readOwnChartTableView(chartTables?.[chart.id]) !== undefined)),
      };
    });
    list.sort(byName);
    return list;
  }, [systemPresetsData, sharedFilters, chartTables]);

  const sharedPresets = useMemo<DashboardPresetItem[]>(() => {
    const list = (sharedPresetsData?.items ?? []).map<DashboardPresetItem>((preset) => ({
      id: buildSharedDashboardPresetId(preset.id),
      ...toSharedPresetListFields(preset),
      charts: preset.settings.charts,
      filters: readServerFilters(preset.settings),
      isSystem: false,
      hasUnsavedChanges: false,
    }));
    list.sort(byName);
    return list;
  }, [sharedPresetsData]);

  const personalPresets = useMemo<DashboardPresetItem[]>(() => {
    const list = Object.values(dashboardPresets.presets ?? {}).map<DashboardPresetItem>((preset) => ({
      ...preset,
      isSystem: false,
      isShared: false,
      isEditable: true,
      hasUnsavedChanges: false,
    }));
    list.sort(byName);
    return list;
  }, [dashboardPresets.presets]);

  // Порядок тот же, что у пресетов таблицы: общий для всех набор, затем совместные, затем личные
  const presets = useMemo(
    () => [...systemPresets, ...sharedPresets, ...personalPresets],
    [systemPresets, sharedPresets, personalPresets],
  );

  // Отказ запроса серверных дашбордов — не пустой список: до ответа отсутствие дашборда ничего не означает
  const isPresetListKnown = isQueryDataReceived(systemPresetsQuery) && isQueryDataReceived(sharedPresetsQuery);

  const activePresetId = dashboardPresets.activePresetId;
  const activePreset = useMemo(
    () => presets.find((preset) => preset.id === activePresetId) ?? null,
    [presets, activePresetId],
  );

  /**
   * Фильтры дашборда для текущего пользователя: у того, кто серверный дашборд не правит, и у автора
   * системного — свои, если он их менял.
   */
  const resolveFilters = useCallback(
    (preset: DashboardPresetItem): DashboardPresetFilters | null => {
      const ownFilters = isOwnFiltersHolder(preset) ? sharedFilters?.[preset.id] : undefined;
      return ownFilters ?? preset.filters ?? null;
    },
    [sharedFilters],
  );

  const activeFilters = useMemo(
    () => (activePreset ? resolveFilters(activePreset) : null),
    [activePreset, resolveFilters],
  );

  const createPreset = useCallback(
    (name: string, charts: CustomChartDefinition[], filters: DashboardPresetFilters): string =>
      createDashboardPreset(userId, name, charts, filters),
    [createDashboardPreset, userId],
  );

  /**
   * Записывает название, состав и/или фильтры. Системный и совместный дашборды записываются на
   * сервере, поэтому итог приходит обещанием: `false` — сервер правку не принял, о причине сообщил
   * общий перехватчик.
   */
  const updatePreset = useCallback(
    async (presetId: string, changes: DashboardPresetChanges): Promise<boolean> => {
      const isWritten = await writeServerPreset(presetId, changes, 'charts');
      if (isWritten !== null) return isWritten;
      updateDashboardPreset(userId, presetId, changes);
      return true;
    },
    [updateDashboardPreset, writeServerPreset, userId],
  );

  /**
   * Запоминает смену фильтров применённого дашборда там, откуда они взяты: у личного — в нём самом,
   * у совместного, которым пользователь владеет, — на сервере, у прочих и у автора системного — за ним
   * самим (у автора это несохранённое изменение). `false` — сервер смену не принял, о причине сообщил
   * общий перехватчик.
   */
  const setActiveFilters = useCallback(
    async (filters: DashboardPresetFilters): Promise<boolean> => {
      if (!activePreset) return false;
      if (isOwnFiltersHolder(activePreset)) {
        setDashboardSharedFilters(userId, activePreset.id, filters);
        return true;
      }
      const isWritten = await writeServerPreset(activePreset.id, { filters }, 'filters');
      if (isWritten !== null) return isWritten;
      updateDashboardPreset(userId, activePreset.id, { filters });
      return true;
    },
    [activePreset, updateDashboardPreset, setDashboardSharedFilters, writeServerPreset, userId],
  );

  /**
   * «Сбросить фильтры» на применённом дашборде. Тот, кто серверный дашборд не правит, и автор
   * системного возвращаются к его фильтрам — и дальше их правка снова доходит до них, — у остальных
   * дашборд получает фильтры по умолчанию.
   */
  const resetActiveFilters = useCallback(
    async (defaults: DashboardPresetFilters): Promise<boolean> => {
      if (activePreset && isOwnFiltersHolder(activePreset)) {
        setDashboardSharedFilters(userId, activePreset.id, null);
        return true;
      }
      return setActiveFilters(defaults);
    },
    [activePreset, setDashboardSharedFilters, setActiveFilters, userId],
  );

  /**
   * Забывает свои фильтры пользователя у серверных дашбордов, которых в списке нет. Зовётся только
   * с полученным списком (`isPresetListKnown`): до него пропавшим выглядит любой серверный.
   */
  const pruneSharedFilters = useCallback(
    () => pruneDashboardSharedFilters(userId, new Set([...systemPresets, ...sharedPresets].map((preset) => preset.id))),
    [pruneDashboardSharedFilters, systemPresets, sharedPresets, userId],
  );

  const apply = useCallback(
    (presetId: string) => applyDashboardPreset(userId, presetId),
    [applyDashboardPreset, userId],
  );

  const clearActive = useCallback(() => clearActiveDashboardPreset(userId), [clearActiveDashboardPreset, userId]);

  /**
   * Удаляет дашборд: системный — у всех, у получателя совместного — убирает только из его списка.
   * `onRemoved` — когда дашборд удалён: у серверного по ответу сервера, отказ его не вызывает.
   */
  const remove = useCallback(
    (presetId: string, onRemoved?: () => void) => {
      // Серверный пропадает из списка только с ответом сервера, поэтому и применение снимается
      // по ответу: отказ оставляет дашборд применённым
      const isServerPreset = deleteServerPreset(presetId, () => {
        if (activePresetId === presetId) clearActiveDashboardPreset(userId);
        onRemoved?.();
      });
      if (isServerPreset) return;
      deleteDashboardPreset(userId, presetId);
      onRemoved?.();
    },
    [deleteDashboardPreset, deleteServerPreset, clearActiveDashboardPreset, userId, activePresetId],
  );

  const setPinned = useCallback(
    (presetId: string, pinned: boolean) => {
      if (!pinServerPreset(presetId, pinned)) setDashboardPresetPinned(userId, presetId, pinned);
    },
    [setDashboardPresetPinned, pinServerPreset, userId],
  );

  const findPersonalTransfer = useCallback(
    (presetId: string): SharedPresetTransfer<DashboardPresetContent> | undefined => {
      const preset = personalPresets.find((item) => item.id === presetId);
      return (
        preset && {
          name: preset.name,
          settings: { charts: preset.charts, filters: preset.filters },
          pinned: preset.pinned,
        }
      );
    },
    [personalPresets],
  );

  const findSharedTransfer = useCallback(
    (presetId: string): SharedPresetTransfer<DashboardPresetContent> | undefined => {
      const preset = sharedPresets.find((item) => item.id === presetId);
      return (
        preset && {
          name: preset.name,
          settings: { charts: preset.charts, filters: preset.filters },
          pinned: preset.pinned,
        }
      );
    },
    [sharedPresets],
  );

  const replaceWithShared = useCallback(
    (personalId: string, created: SharedPreset<DashboardPresetContent>, wasActive: boolean) => {
      // Применённость переходит на совместный до удаления личного — иначе удаление сняло бы её
      if (wasActive) applyDashboardPreset(userId, buildSharedDashboardPresetId(created.id));
      deleteDashboardPreset(userId, personalId);
    },
    [applyDashboardPreset, deleteDashboardPreset, userId],
  );

  const replaceWithPersonal = useCallback(
    (_sharedPresetId: string, preset: SharedPresetTransfer<DashboardPresetContent>, wasActive: boolean) => {
      // Личный — свой состав, а не ссылка на состав из ответа сервера
      const { charts, filters } = preset.settings;
      createDashboardPreset(
        userId,
        preset.name,
        structuredClone(charts),
        filters ? structuredClone(filters) : undefined,
        {
          pinned: preset.pinned,
          apply: wasActive,
        },
      );
    },
    [createDashboardPreset, userId],
  );

  /**
   * Поделиться дашбордом: личный становится совместным с теми же названием, составом, фильтрами
   * и закреплением, у совместного меняются получатели, без получателей он возвращается в личные.
   */
  const { share, isSharing } = useSharePreset<DashboardPresetContent>({
    tableName: DASHBOARD_PRESETS_TABLE_NAME,
    activePresetId,
    parseSharedId: parseSharedDashboardPresetId,
    findPersonal: findPersonalTransfer,
    findShared: findSharedTransfer,
    replaceWithShared,
    replaceWithPersonal,
  });

  /**
   * Делает личный дашборд системным: системный получает его название, состав и фильтры, личный по
   * ответу сервера уходит из списка, а применённость переходит на системный. Закрепление не
   * переносится: у личного оно личное, у системного — общее для всех. Ответ сервера возвращается
   * вызывающему: пока он не пришёл, повторное нажатие завело бы второй системный дашборд.
   */
  const makeSystem = useCallback(
    async (presetId: string) => {
      const preset = personalPresets.find((item) => item.id === presetId);
      if (!preset) return;
      await createSystemPreset(
        {
          tableName: DASHBOARD_PRESETS_TABLE_NAME,
          name: preset.name,
          settings: { charts: preset.charts, filters: preset.filters },
        },
        {
          onSuccess: (created) => {
            // Применённость переходит на системный до удаления личного — иначе удаление сняло бы её
            if (activePresetId === presetId) applyDashboardPreset(userId, buildSystemDashboardPresetId(created.id));
            deleteDashboardPreset(userId, presetId);
          },
        },
      );
    },
    [createSystemPreset, applyDashboardPreset, deleteDashboardPreset, personalPresets, activePresetId, userId],
  );

  /**
   * Состав дашборда таким, каким его видит пользователь: у того, кто вид колонок меняет у себя, —
   * с его видом колонок поверх вида дашборда.
   */
  const resolveCharts = useCallback(
    (preset: DashboardPresetItem): CustomChartDefinition[] => {
      if (!isOwnFiltersHolder(preset)) return preset.charts;
      return preset.charts.map((chart) => {
        const ownView = readOwnChartTableView(chartTables?.[chart.id]);
        return ownView ? withChartTableView(chart, ownView) : chart;
      });
    },
    [chartTables],
  );

  /**
   * Снимает у автора системного дашборда его несохранённые изменения — и фильтры, и вид колонок
   * таблиц: дашборд у него снова такой, каким записан.
   */
  const dropUnsavedChanges = useCallback(
    (preset: DashboardPresetItem) => {
      setDashboardSharedFilters(userId, preset.id, null);
      clearDashboardChartTableViews(
        userId,
        preset.charts.map((chart) => chart.id),
      );
    },
    [setDashboardSharedFilters, clearDashboardChartTableViews, userId],
  );

  /**
   * Заводит личную копию дашборда под переданным названием и применяет её: сам дашборд не меняется.
   * Копия — у любого дашборда, в том числе своего: она получает состав с раскладкой и оформлением,
   * фильтры и вид колонок таблиц, которые пользователь видит. Несохранённые изменения автора
   * системного копия забирает с собой: с системного они снимаются.
   * @returns Идентификатор копии.
   */
  const saveCopy = useCallback(
    (presetId: string, name: string): string | null => {
      const preset = presets.find((item) => item.id === presetId);
      if (!preset) return null;
      const filters = resolveFilters(preset);
      // Копия — свой состав, а не ссылка на состав из ответа сервера
      const copyId = createDashboardPreset(
        userId,
        name,
        structuredClone(resolveCharts(preset)),
        filters ? structuredClone(filters) : undefined,
      );
      if (preset.hasUnsavedChanges) dropUnsavedChanges(preset);
      return copyId;
    },
    [createDashboardPreset, presets, resolveFilters, resolveCharts, dropUnsavedChanges, userId],
  );

  /**
   * «Отменить изменения» у автора системного дашборда: снимает все его несохранённые изменения
   * применённого дашборда — и фильтры, и вид колонок, — не трогая сервер.
   */
  const discardUnsavedChanges = useCallback(() => {
    if (activePreset?.hasUnsavedChanges) dropUnsavedChanges(activePreset);
  }, [activePreset, dropUnsavedChanges]);

  /**
   * Записывает несохранённые изменения автора системного дашборда — фильтры и вид колонок — в сам
   * дашборд содержимым целиком и снимает их у автора. `false` — сервер запись не принял: изменения
   * остаются несохранёнными, о причине сообщил общий перехватчик.
   */
  const saveUnsavedChanges = useCallback(async (): Promise<boolean> => {
    if (!activePreset?.hasUnsavedChanges) return false;
    const filters = resolveFilters(activePreset);
    const isWritten = await writeServerPreset(
      activePreset.id,
      { charts: resolveCharts(activePreset), ...(filters ? { filters } : {}) },
      'filters',
    );
    if (!isWritten) return false;
    dropUnsavedChanges(activePreset);
    return true;
  }, [activePreset, resolveFilters, resolveCharts, writeServerPreset, dropUnsavedChanges]);

  /** Смена вида колонок у того, кто вид дашборда пишет: у личного — в нём самом, у совместного — на сервере. */
  const writeDashboardTableView = useCallback(
    async (chartId: string, view: CustomChartTableView | undefined): Promise<boolean> => {
      if (!activePreset) return false;
      const charts = activePreset.charts.map((chart) =>
        chart.id === chartId ? withChartTableView(chart, view) : chart,
      );
      const isWritten = await writeServerPreset(activePreset.id, { charts }, 'filters');
      if (isWritten !== null) return isWritten;
      updateDashboardPreset(userId, activePreset.id, { charts });
      return true;
    },
    [activePreset, writeServerPreset, updateDashboardPreset, userId],
  );

  /**
   * Свой вид колонок таблицы диаграммы применённого дашборда поверх вида дашборда: есть только у
   * того, кто вид колонок меняет у себя, и только пока он его менял.
   */
  const readOwnTableView = useCallback(
    (chartId: string): CustomChartTableView | undefined =>
      activePreset && isOwnFiltersHolder(activePreset) ? readOwnChartTableView(chartTables?.[chartId]) : undefined,
    [activePreset, chartTables],
  );

  /**
   * Запоминает смену вида колонок таблицы применённого дашборда тем же путём, что фильтры: у личного —
   * в нём самом, у совместного, которым пользователь владеет, — на сервере, у прочих и у автора
   * системного — за ним самим поверх вида дашборда. `false` — сервер смену не принял.
   */
  const setActiveTableView = useCallback(
    async (chartId: string, view: CustomChartTableView): Promise<boolean> => {
      if (!activePreset) return false;
      if (isOwnFiltersHolder(activePreset)) {
        // Свой вид пишется целиком, с шириной колонок: по ней он и отличается от записи прежнего клиента
        setDashboardChartTableSettings(userId, chartId, {
          visibleColumns: view.visibleColumns,
          columnOrder: view.columnOrder,
          columnSizing: view.columnSizing ?? {},
        });
        return true;
      }
      return writeDashboardTableView(chartId, view);
    },
    [activePreset, setDashboardChartTableSettings, writeDashboardTableView, userId],
  );

  /**
   * «Очистить» в панели колонок таблицы применённого дашборда: у того, кто вид колонок меняет у себя,
   * снимает его вид — снова действует вид дашборда, — у остальных снимает вид дашборда, и таблица
   * показана всеми колонками выборки.
   */
  const clearActiveTableView = useCallback(
    async (chartId: string): Promise<boolean> => {
      if (!activePreset) return false;
      if (isOwnFiltersHolder(activePreset)) {
        clearDashboardChartTableViews(userId, [chartId]);
        return true;
      }
      return writeDashboardTableView(chartId, undefined);
    },
    [activePreset, clearDashboardChartTableViews, writeDashboardTableView, userId],
  );

  /**
   * Переводит настройку колонок, которую прежний клиент запомнил за владельцем, в вид колонок его
   * применённого дашборда — у таблиц, вида колонок в дашборде ещё не имеющих. Перенесённая настройка
   * у владельца снимается; отказ сервера оставляет её на месте.
   */
  const adoptLegacyTableViews = useCallback(async (): Promise<void> => {
    if (!activePreset?.isEditable) return;
    const adopted = new Map<string, CustomChartTableView>();
    activePreset.charts.forEach((chart) => {
      const legacyView = readLegacyChartTableView(chart, chartTables?.[chart.id]);
      if (legacyView) adopted.set(chart.id, legacyView);
    });
    if (adopted.size === 0) return;
    const charts = activePreset.charts.map((chart) =>
      withChartTableView(chart, adopted.get(chart.id) ?? chart.tableView),
    );
    const isWritten = await writeServerPreset(activePreset.id, { charts }, 'filters');
    if (isWritten === false) return;
    if (isWritten === null) updateDashboardPreset(userId, activePreset.id, { charts });
    clearDashboardChartTableViews(userId, [...adopted.keys()]);
  }, [activePreset, chartTables, writeServerPreset, updateDashboardPreset, clearDashboardChartTableViews, userId]);

  const setChartTableSettings = useCallback(
    (chartId: string, settings: Partial<CustomChartTableSettings>) =>
      setDashboardChartTableSettings(userId, chartId, settings),
    [setDashboardChartTableSettings, userId],
  );

  const setChartTablesColumnsPanel = useCallback(
    (patch: DashboardChartTablesColumnsPanel) => setDashboardChartTablesColumnsPanel(userId, patch),
    [setDashboardChartTablesColumnsPanel, userId],
  );

  return {
    presets,
    isPresetListKnown,
    /**
     * Системные или совместные дашборды ещё загружаются; после отказа запроса — уже нет, хотя список
     * и не получен
     */
    isPresetListLoading: systemPresetsQuery.isLoading || sharedPresetsQuery.isLoading,
    /**
     * Пользователь вправе делать личные дашборды системными; править, закреплять и удалять системный
     * может только его автор (`isEditable`)
     */
    canManageSystemPresets,
    activePresetId,
    activePreset,
    /** Фильтры панели применённого дашборда; `null` — своих фильтров нет, действуют фильтры по умолчанию */
    activeFilters,
    /**
     * Фильтры и вид колонок применённого дашборда пользователь меняет у себя, а не в самом дашборде:
     * дашборд он не правит либо дашборд системный
     */
    isActiveChangeOwn: activePreset ? isOwnFiltersHolder(activePreset) : false,
    /** Личные настройки таблиц диаграмм по идентификатору диаграммы: упорядочение и фильтры колонок */
    chartTables,
    /** Ширина и закрепление панели колонок — общие для всех таблиц диаграмм; нет — умолчание */
    chartTablesColumnsPanel: dashboardPresets.chartTablesColumnsPanel,
    /** Запись состава серверного дашборда — системного или совместного — в работе */
    isUpdating,
    isSharing,
    createPreset,
    updatePreset,
    setActiveFilters,
    resetActiveFilters,
    pruneSharedFilters,
    apply,
    clearActive,
    remove,
    setPinned,
    share,
    makeSystem,
    /** Личный дашборд переводится в системные — до ответа сервера повторно не переводится */
    isMakingSystem,
    saveCopy,
    saveUnsavedChanges,
    discardUnsavedChanges,
    setChartTableSettings,
    setChartTablesColumnsPanel,
    readOwnTableView,
    setActiveTableView,
    clearActiveTableView,
    adoptLegacyTableViews,
  };
};
